<template>
  <div>
    <transition name="fade" mode="out-in"> </transition>

    <QuestionnaireCard
      v-if="questionIndex === -1"
      :answer="true"
      key="0"
      can-go-next
      @onNext="(answer) => onNext({ questionId: 0 }, answer)"
    >
      <template #title>
        <HeaderTypo as="h2">{{ $t('areYouInUAE') }}</HeaderTypo>
      </template>
    </QuestionnaireCard>
    <template v-if="questionIndex > -1 && questionnaire.length > 0">
      <template v-if="questionIndex >= 0 && questionIndex < questionnaire.length">
        <template v-for="(question, key) in questionnaire">
          <QuestionnaireCard
            v-if="questionIndex == key"
            :key="key"
            :answer="answers[question.questionId] || false"
            can-go-back
            can-check
            can-go-next
            @onBack="onBack"
            @onCheck="(answer) => onCheck(question, answer)"
            @onNext="(answer) => onNext(question, answer)"
          >
            <template #title>
              <div v-html="question.questionContentEnglish"></div>
            </template>
          </QuestionnaireCard>
        </template>
      </template>
      <div v-else class="flex flex-col gap-y-3">
        <HeaderTypo as="h2" class="pb-3 border-b border-brand-gray-300">
          {{ $t('checkEligibilityResult') }}
        </HeaderTypo>
        <div v-if="results.length > 0" class="flex flex-col gap-y-1">
          <p>{{ $t('checkEligibilitySuccessMessage') }}</p>
          <p>
            {{ $t('checkEligibilityFAQMessage') }}
            <RouterLink to="/faq" class="text-primary font-medium">{{ $t('fAQPage') }}</RouterLink>
          </p>
          <div :key="$i18n.locale" class="pt-4 flex flex-wrap">
            <div class="w-full h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <GoldenServiceCard
                v-for="(service, key) in results"
                :key="key"
                :service-id="service.serviceTransactionId"
                :config="{
                  serviceCard: false,
                  startService: true,
                  trackApplication: false
                }"
              >
                <div class="w-full p-1 flex flex-col items-center gap-y-2">
                  <span class="text-center">{{ service.reasonName }}</span>
                  <span class="text-center">{{ service.serviceTransactionName }}</span>
                </div>
              </GoldenServiceCard>
            </div>
          </div>
        </div>
        <p v-else>{{ $t('checkEligibilityFailedMessage') }}</p>
      </div>
    </template>
    <template v-else-if="questionnaire.length === 0 && questionIndex > -1">no data</template>
  </div>
</template>

<script setup>
import eligibility_questionnaire from '@/assets/json/eligibility_questionnaire.json'
import QuestionnaireCard from '@/components/cards/QuestionnaireCard.vue'
import { useLookupsStore } from '@/stores/lookups'
import { useGlobalStore } from '@/stores/global'
import { onMounted, ref, computed } from 'vue'
import GoldenServiceCard from '@/components/cards/GoldenServiceCard.vue'

const globalStore = useGlobalStore()
const lookupsStore = useLookupsStore()

const serviceTransactionIds = [632, 633, 634, 635, 636, 753, 1104, 1105]

const questionIndex = ref(-1)
// debug only
// const questionIndex = ref(999)

const questionnaire = ref({})
const transactionReasons = ref([])
const serviceNames = ref([])

const initialAnswer = ref(false)

const answers = ref({})
// debug only
// const answers = ref({ 1: true })

onMounted(async () => {
  // debug only
  // await getServiceNameDetails()
  // await getTransactionReasons()
  // await fetchQuestionnaire({ questionId: 0 }, true)
})

const logPayload = computed(() => {
  const yesQuestionsIds = []
  const noQuestionsIds = []

  Object.entries(answers.value).forEach(([questionId, answer]) => {
    if (answer) {
      yesQuestionsIds.push(questionId)
    } else {
      noQuestionsIds.push(questionId)
    }
  })

  return {
    eligibilityCategoryId: 1,
    serviceTransactionIds: '',
    personSubCategoryIds: '',
    yesQuestionsIds,
    noQuestionsIds
  }
})

const results = computed(() => {
  const yesAnswers = eligibility_questionnaire.filter((q) => {
    return logPayload.value.yesQuestionsIds.map((y) => +y).includes(q.questionId)
  })

  const resultsArray = []

  yesAnswers.forEach((answer) => {
    const { personSubCategoryIds, preRequestPersonSubCategoryIds } = answer
    const reasonIds = personSubCategoryIds.split(',').map((x) => +x)
    const preReasonIds = preRequestPersonSubCategoryIds?.split(',').map((x) => +x)

    reasonIds.forEach((reasonId) => {
      let currentPreServiceTransactionId = 0
      if (preReasonIds && preReasonIds.some((p) => p === reasonId)) {
        currentPreServiceTransactionId = answer.preRequestServiceTransactionId
      }

      resultsArray.push({
        serviceTransactionId: answer.serviceTransactionId,
        reasonId,
        preServiceTransactionId: currentPreServiceTransactionId,
        serviceTransactionName: serviceNames.value.find(
          (sn) => sn.serviceTransId == answer.serviceTransactionId
        )?.serviceShortDescription,
        reasonName: transactionReasons.value.find((reason) => reason.code === reasonId)?.name
      })
    })
  })

  // debug only
  // const resultsArray = [
  //   {
  //     serviceTransactionId: 632,
  //     reasonId: 770,
  //     preServiceTransactionId: 0,
  //     serviceTransactionName:
  //       'VISA - GOLDEN RESIDENCE - INVESTOR IN PUBLIC INVESTMENTS - ISSUE NEW VISA',
  //     reasonName: 'Investor In Public Investments'
  //   },
  //   {
  //     serviceTransactionId: 633,
  //     reasonId: 771,
  //     preServiceTransactionId: 0,
  //     serviceTransactionName: 'VISA - GOLDEN RESIDENCE - INVESTOR IN REAL ESTATE - ISSUE NEW VISA',
  //     reasonName: 'Real Estate Investor'
  //   },
  //   {
  //     serviceTransactionId: 634,
  //     reasonId: 772,
  //     preServiceTransactionId: 0,
  //     serviceTransactionName: 'VISA - GOLDEN RESIDENCE - ENTREPRENEUR - ISSUE NEW VISA',
  //     reasonName: 'Entrepreneurs'
  //   },
  //   {
  //     serviceTransactionId: 636,
  //     reasonId: 773,
  //     preServiceTransactionId: 767,
  //     serviceTransactionName:
  //       "VISA - GOLDEN RESIDENCE - HIGH SCHOOL'S TOP RANKED STUDENTS IN UAE - ISSUE NEW VISA",
  //     reasonName: 'High School-s Top Ranked Students in UAE'
  //   },
  //   {
  //     serviceTransactionId: 753,
  //     reasonId: 774,
  //     preServiceTransactionId: 767,
  //     serviceTransactionName:
  //       'VISA - GOLDEN RESIDENCE - GRADUATES OF ACCREDITED UNIVERSITIES IN UAE - ISSUE NEW VISA',
  //     reasonName: 'Graduates of Accredited Universities in UAE'
  //   },
  //   {
  //     serviceTransactionId: 635,
  //     reasonId: 552,
  //     preServiceTransactionId: 767,
  //     serviceTransactionName:
  //       'VISA - GOLDEN RESIDENCE - PERSONS WHO ARE TALENTED PIONEERS - ISSUE NEW VISA',
  //     reasonName: 'SCIENTISTS AND SPECIALISTS - HEALTH PROFESSIONALS'
  //   }
  // ]

  return resultsArray
})

const onBack = () => {
  questionIndex.value -= 1
}

/**
 * this should finish the questionnaire and go to results
 */
const onCheck = (question, answer) => {
  answers.value[question.questionId] = answer
  questionIndex.value = 999
}

const onNext = async (question, answer) => {
  if (question.questionId === 0) {
    await getServiceNameDetails()
    await getTransactionReasons()
    await fetchQuestionnaire(answer)
    initialAnswer.value = answer
  }

  if (question.questionId > 0) {
    answers.value[question.questionId] = answer
  }
  questionIndex.value += 1
}

const fetchQuestionnaire = async (answer) => {
  // const data = await axios.post('/api/anonymous/goldenVisaEligibilityQuestion/get', {
  //   eligibilityCategoryId: 1,
  //   isActive: 1,
  //   insideUAE: answer ? 1 : 0
  // })

  questionnaire.value = eligibility_questionnaire
}

async function getServiceNameDetails() {
  const names = await lookupsStore.getServiceNameDetails()

  serviceNames.value = names.filter((n) => {
    return serviceTransactionIds.includes(n.serviceTransId)
  })
}

async function getTransactionReasons() {
  const promises = serviceTransactionIds.map(async (id) => {
    return await lookupsStore.getTransactionReasonsByModuleParentReasonId(id)
  })

  const reasons = await Promise.all(promises)

  reasons.forEach((reasonList) => {
    transactionReasons.value.push(...reasonList.map((l) => ({ name: l.localizedName, code: l.id })))
  })
}
</script>

<style lang="scss" scoped></style>
